import React, { useState, useEffect } from 'react';
import { X, Check, Clock } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { equipmentCategories, injuryRegions, injurySystemic, goalOptions, experienceLevels } from '../data/equipment';
import { recoveryActivities } from '../data/endurance';
import { programs } from '../data/programs';
import { REST_OPTIONS, DEFAULT_REST_SECONDS } from '../data/exercises';

// Flattened list of every selectable equipment id — used to "downgrade" from
// Full gym into a custom list that starts fully selected (user removes what they lack).
const ALL_EQUIPMENT_IDS = equipmentCategories.flatMap((c) => c.items.map((i) => i.id));
import { martialArts } from '../data/training';

/**
 * EditProfileModal — Section-scoped profile editor.
 *
 * One modal that adapts based on `section` prop to surface just the relevant
 * fields. Saves through ProfileContext.update() so cloud sync auto-fires.
 * Closes without navigating away — user stays on Profile page.
 */
const ChipToggle = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
      active
        ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
        : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
    }`}>
    {children}
  </button>
);

const SquareCard = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`p-3 rounded-md text-sm border text-left transition-colors ${
      active
        ? 'bg-enf-accent/10 border-enf-accent-fg'
        : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
    }`}>
    {children}
  </button>
);

const EditProfileModal = ({ section, open, onClose }) => {
  const { profile, update } = useProfile();
  const [draft, setDraft] = useState(profile);

  useEffect(() => {
    if (open) setDraft(profile);
  }, [open, profile]);

  if (!open || !section) return null;

  const save = () => {
    update(draft);
    onClose();
  };

  // Helpers
  const toggleArray = (key, id) => {
    const cur = draft[key] || [];
    setDraft({ ...draft, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  // Equipment chip: from Full gym, first tap drops fullGym but keeps everything
  // else selected (the "deselect a few" path). Otherwise a normal toggle.
  const toggleEquipmentItem = (id) => {
    if (draft.fullGym) {
      setDraft({ ...draft, fullGym: false, noEquipment: false, equipment: ALL_EQUIPMENT_IDS.filter((x) => x !== id) });
      return;
    }
    toggleArray('equipment', id);
  };
  // "Choose specific" preset: clear the board for users with only a few pieces.
  const chooseSpecificEquipment = () => setDraft({ ...draft, fullGym: false, noEquipment: false, equipment: [] });
  const toggleInjury = (id) => {
    const inj = { ...(draft.injuries || {}) };
    inj[id] = !inj[id];
    setDraft({ ...draft, injuries: inj });
  };
  const toggleMartialArt = (id) => {
    const cur = draft.martialArts || [];
    setDraft({ ...draft, martialArts: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const setRank = (artId, rankId) => {
    setDraft({ ...draft, ranks: { ...(draft.ranks || {}), [artId]: { rankId, stripes: 0 } } });
  };

  // Section bodies
  const SECTION_CONFIG = {
    personal: {
      title: 'Personal info',
      body: (
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Name</span>
            <input type="text" value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              data-testid="edit-name" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Email</span>
            <input type="email" value={draft.email || ''} onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              data-testid="edit-email" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Age</span>
              <input type="number" min={13} max={100} value={draft.age || ''} onChange={(e) => setDraft({ ...draft, age: e.target.value })}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="edit-age" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Weight (lb)</span>
              <input type="number" min={60} max={500} value={draft.weight || ''} onChange={(e) => setDraft({ ...draft, weight: e.target.value })}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="edit-weight" />
            </label>
          </div>
          <div>
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Sex</span>
            <div className="grid grid-cols-2 gap-2">
              {[{ id: 'male', label: 'Male' }, { id: 'female', label: 'Female' }].map((o) => (
                <ChipToggle key={o.id} active={draft.sex === o.id} onClick={() => setDraft({ ...draft, sex: o.id })} testid={`edit-sex-${o.id}`}>
                  {o.label}
                </ChipToggle>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Height (ft)</span>
              <input type="number" min={3} max={7} value={draft.heightFt || ''} onChange={(e) => setDraft({ ...draft, heightFt: e.target.value })}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="edit-height-ft" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Height (in)</span>
              <input type="number" min={0} max={11} value={draft.heightIn || 0} onChange={(e) => setDraft({ ...draft, heightIn: e.target.value })}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="edit-height-in" />
            </label>
          </div>
        </div>
      ),
    },
    experience: {
      title: 'Experience level',
      body: (
        <div className="grid grid-cols-1 gap-2">
          {experienceLevels.map((e) => (
            <SquareCard key={e.id} active={draft.experience === e.id} onClick={() => setDraft({ ...draft, experience: e.id })} testid={`edit-exp-${e.id}`}>
              <div className="font-semibold mb-0.5">{e.name}</div>
              <div className="text-xs text-enf-text-secondary">{e.description}</div>
            </SquareCard>
          ))}
        </div>
      ),
    },
    commitment: {
      title: 'Commitment',
      body: (
        <div className="space-y-5">
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">Days per week</span>
              <span className="font-display text-2xl text-enf-accent-fg">{draft.daysPerWeek}</span>
            </div>
            <input type="range" min={2} max={7} value={draft.daysPerWeek} onChange={(e) => setDraft({ ...draft, daysPerWeek: Number(e.target.value) })}
              className="w-full" data-testid="edit-days-per-week" />
            <div className="flex justify-between text-[10px] text-enf-text-muted mt-1"><span>2</span><span>7</span></div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Minutes per session</span>
            <div className="grid grid-cols-3 gap-2">
              {[30, 45, 60].map((m) => (
                <ChipToggle key={m} active={draft.minutesPerSession === m} onClick={() => setDraft({ ...draft, minutesPerSession: m })} testid={`edit-mins-${m}`}>
                  {m} min
                </ChipToggle>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Rest between sets</span>
            <div className="grid grid-cols-5 gap-2">
              {REST_OPTIONS.map((s) => (
                <ChipToggle key={s} active={(draft.restSeconds ?? DEFAULT_REST_SECONDS) === s} onClick={() => setDraft({ ...draft, restSeconds: s })} testid={`edit-rest-${s}`}>
                  {s}s{s === DEFAULT_REST_SECONDS ? <span className="block text-[9px] font-normal opacity-70">Best fit</span> : null}
                </ChipToggle>
              ))}
            </div>
            {(draft.restSeconds ?? DEFAULT_REST_SECONDS) > DEFAULT_REST_SECONDS && (
              <div className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 rounded-md bg-enf-role-warning/10 border border-enf-role-warning/30 text-xs font-medium text-enf-role-warning">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                Longer rests mean longer sessions.
              </div>
            )}
          </div>
        </div>
      ),
    },
    program: {
      title: 'Training program',
      body: (
        <div className="space-y-2">
          {Object.entries(programs).map(([id, p]) => (
            <SquareCard key={id} active={draft.programType === id}
              onClick={() => setDraft({ ...draft, programType: id, schedule: null })}
              testid={`edit-program-${id}`}>
              <div className="font-semibold mb-0.5">{p.name}</div>
              <div className="text-xs text-enf-text-secondary">{p.description}</div>
            </SquareCard>
          ))}
          <div className="text-xs text-enf-text-muted italic pt-2">Changing your program resets any custom day-by-day tweaks.</div>
        </div>
      ),
    },
    goals: {
      title: 'Goals',
      body: (
        <div className="flex flex-wrap gap-2">
          {goalOptions.map((g) => (
            <ChipToggle key={g.id} active={(draft.goals || []).includes(g.id)}
              onClick={() => toggleArray('goals', g.id)} testid={`edit-goal-${g.id}`}>
              {g.name}
            </ChipToggle>
          ))}
        </div>
      ),
    },
    limitations: {
      title: 'Limitations & injuries',
      body: (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Body regions</div>
            <div className="flex flex-wrap gap-2">
              {injuryRegions.map((r) => (
                <ChipToggle key={r.id} active={!!(draft.injuries || {})[r.id]}
                  onClick={() => toggleInjury(r.id)} testid={`edit-injury-${r.id}`}>
                  {r.name}
                </ChipToggle>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Systemic</div>
            <div className="flex flex-wrap gap-2">
              {injurySystemic.map((r) => (
                <ChipToggle key={r.id} active={!!(draft.injuries || {})[r.id]}
                  onClick={() => toggleInjury(r.id)} testid={`edit-systemic-${r.id}`}>
                  {r.name}
                </ChipToggle>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Notes for your coach</span>
            <textarea rows={3} value={draft.injuryNotes || ''} onChange={(e) => setDraft({ ...draft, injuryNotes: e.target.value })}
              placeholder="Anything else we should know? Recent surgeries, ranges to avoid, etc."
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none"
              data-testid="edit-injury-notes" />
          </label>
        </div>
      ),
    },
    equipment: {
      title: 'Equipment access',
      body: (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <ChipToggle active={!!draft.fullGym} onClick={() => setDraft({ ...draft, fullGym: !draft.fullGym, noEquipment: false })} testid="edit-fullgym">
              Full gym access
            </ChipToggle>
            <ChipToggle active={!draft.fullGym && !draft.noEquipment} onClick={chooseSpecificEquipment} testid="edit-choose-equip">
              Choose specific
            </ChipToggle>
            <ChipToggle active={!!draft.noEquipment} onClick={() => setDraft({ ...draft, noEquipment: !draft.noEquipment, fullGym: false, equipment: [] })} testid="edit-noequip">
              No equipment
            </ChipToggle>
          </div>
          {draft.fullGym && (
            <p className="text-xs text-enf-text-muted">
              Tap any item below to remove it — you'll switch to a custom list with everything else kept.
            </p>
          )}
          {!draft.noEquipment && (
            <div className="space-y-3 max-h-[40vh] overflow-y-auto">
              {equipmentCategories.map((cat) => (
                <div key={cat.category}>
                  <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-bold mb-1.5">{cat.category}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.items.map((item) => (
                      <ChipToggle key={item.id}
                        active={(draft.equipment || []).includes(item.id) || !!draft.fullGym}
                        onClick={() => toggleEquipmentItem(item.id)}
                        testid={`edit-equip-${item.id}`}>
                        {item.name}
                      </ChipToggle>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
    recovery: {
      title: 'Endurance activities',
      body: (
        <div className="flex flex-wrap gap-2">
          {recoveryActivities.map((r) => (
            <ChipToggle key={r.id} active={(draft.recoveryActivities || []).includes(r.id)}
              onClick={() => toggleArray('recoveryActivities', r.id)} testid={`edit-recovery-${r.id}`}>
              {r.name}
            </ChipToggle>
          ))}
        </div>
      ),
    },
    martial: {
      title: 'Martial arts',
      body: (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Practiced arts</div>
            <div className="flex flex-wrap gap-2">
              {martialArts.map((a) => (
                <ChipToggle key={a.id} active={(draft.martialArts || []).includes(a.id)}
                  onClick={() => toggleMartialArt(a.id)} testid={`edit-martial-${a.id}`}>
                  {a.name}
                </ChipToggle>
              ))}
            </div>
          </div>
          {(draft.martialArts || []).length > 0 && (
            <div className="space-y-3 max-h-[40vh] overflow-y-auto">
              {(draft.martialArts || []).map((artId) => {
                const art = martialArts.find((a) => a.id === artId);
                const ranks = (art?.ranks || []);
                if (!art || ranks.length === 0) return null;
                const currentRankId = (draft.ranks || {})[artId]?.rankId;
                return (
                  <div key={artId}>
                    <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-bold mb-1.5">{art.name} rank</div>
                    <div className="flex flex-wrap gap-1.5">
                      {ranks.map((r) => (
                        <ChipToggle key={r.id} active={currentRankId === r.id}
                          onClick={() => setRank(artId, r.id)} testid={`edit-rank-${artId}-${r.id}`}>
                          {r.name}
                        </ChipToggle>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
  };

  const cfg = SECTION_CONFIG[section];
  if (!cfg) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl max-h-[90vh] overflow-y-auto bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl"
        data-testid={`edit-modal-${section}`}>
        <div className="sticky top-0 bg-enf-surface-deep border-b border-enf-surface-border px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-enf-accent-fg font-bold">Edit · profile</div>
            <div className="font-display text-2xl tracking-wide leading-none">{cfg.title}</div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary p-1" aria-label="Close" data-testid="edit-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{cfg.body}</div>
        <div className="sticky bottom-0 bg-enf-surface-deep border-t border-enf-surface-border px-6 py-4 flex gap-2 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors"
            data-testid="edit-modal-cancel">
            Cancel
          </button>
          <button onClick={save}
            className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center gap-2"
            data-testid="edit-modal-save">
            <Check className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
